import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Ban, Pencil, Trash2, Gift, ChevronDown, ChevronRight,
  MapPin, CalendarClock, Users as UsersIcon, Phone, Clock, History,
} from "lucide-react";
import { PageHeader } from "@/components/EntityPage";

const TONE = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

function BonusDialog({ trip, open, onOpenChange, onSaved }) {
  const [amt, setAmt] = useState(trip?.bonus_amount || 0);
  const [reason, setReason] = useState("");
  useEffect(() => { setAmt(trip?.bonus_amount || 0); setReason(""); }, [trip?.id, open]);
  if (!trip) return null;
  const isEdit = (trip.bonus_amount || 0) > 0;
  async function save() {
    try {
      await api.put(`/driver-trips/${trip.id}/bonus`, { amount: parseFloat(amt) || 0, reason: reason || null });
      toast.success("Bonus saved");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function remove() {
    if (!reason.trim()) { toast.error("Reason is required to remove a bonus"); return; }
    try {
      await api.delete(`/driver-trips/${trip.id}/bonus?reason=${encodeURIComponent(reason)}`);
      toast.success("Bonus removed");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400"/> {isEdit ? "Edit bonus" : "Add bonus"} · {trip.driver_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Bonus amount ₹</Label>
            <Input type="number" min="0" step="1" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="ds-bonus-amt" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Reason / note {isEdit ? "(required to remove)" : "(optional)"}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Exceptional customer service, referral, extra trip length…" data-testid="ds-bonus-reason" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
          <div className="flex justify-between pt-2">
            {isEdit ? (
              <Button variant="outline" onClick={remove} data-testid="ds-bonus-remove" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1">
                <Trash2 className="w-3.5 h-3.5"/> Remove bonus
              </Button>
            ) : <span/>}
            <Button onClick={save} data-testid="ds-bonus-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">
              {isEdit ? "Update bonus" : "Add bonus"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ trip, open, onOpenChange, onSaved, mode }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(""), [trip?.id, open]);
  if (!trip) return null;
  async function submit() {
    if (!reason.trim()) return toast.error("Reason is required");
    try {
      await api.post(`/driver-trips/${trip.id}/${mode}`, { reason });
      toast.success(mode === "cancel" ? "Trip cancelled" : "Trip rejected");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2 text-rose-300">
            <Ban className="w-5 h-5"/> {mode === "cancel" ? "Cancel confirmed trip" : "Reject trip"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">{trip.customer_name} · {trip.pickup_location} → {trip.drop_location} · {trip.trip_date}</div>
          <div>
            <Label className="text-[10px] uppercase text-rose-300">Reason (required)</Label>
            <Input required value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ds-cancel-reason" placeholder="Why is this being cancelled/rejected? Driver will see this reason." className="bg-[hsl(224_40%_12%)] mt-1 border-rose-500/30 focus-visible:ring-rose-500/40"/>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={submit} data-testid="ds-cancel-submit" className="bg-rose-500 text-white hover:bg-rose-400 font-semibold gap-1">
              <Ban className="w-4 h-4"/> {mode === "cancel" ? "Cancel trip" : "Reject trip"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TripRow({ t, onAction }) {
  const [open, setOpen] = useState(false);
  const status = t.status || "pending";
  return (
    <div className="rounded-lg border border-border bg-[hsl(224_45%_9%)]" data-testid={`ds-row-${t.id}`}>
      <div className="px-4 py-3 flex flex-col md:grid md:grid-cols-[auto_1fr_auto] gap-3 md:items-center">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-white p-1 -m-1 shrink-0">
            {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
          </button>
          <button onClick={() => setOpen(!open)} className="text-left min-w-0 flex-1 overflow-x-auto -mx-1 px-1 [scrollbar-width:thin]">
            <div className="grid grid-cols-6 gap-3 items-center min-w-[720px] md:min-w-0">
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Driver</div>
                <div className="text-sm truncate">{t.driver_name || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Customer</div>
                <div className="text-sm truncate">{t.customer_name}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Trip date</div>
                <div className="text-sm font-mono">{t.trip_date}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Route</div>
                <div className="text-sm truncate">{t.pickup_location} → {t.drop_location}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Offer / Bonus</div>
                <div className="text-sm font-mono"><span className="text-sky-300">{fmt(t.trip_offer_value)}</span> {t.bonus_amount > 0 && <span className="text-emerald-300"> + {fmt(t.bonus_amount)}</span>}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Pax · Days</div>
                <div className="text-sm font-mono">{t.no_of_passengers} · {t.no_of_days}d</div>
              </div>
            </div>
          </button>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap md:justify-end shrink-0 border-t border-border/40 md:border-0 pt-2 md:pt-0">
          <Badge className={TONE[status]}>{status}</Badge>
          {status === "pending" && (
            <>
              <Button size="sm" variant="outline" data-testid={`ds-confirm-${t.id}`} onClick={() => onAction("confirm", t)} className="h-8 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-1">
                <CheckCircle2 className="w-3.5 h-3.5"/> Confirm
              </Button>
              <Button size="sm" variant="outline" data-testid={`ds-reject-${t.id}`} onClick={() => onAction("reject", t)} className="h-8 border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1">
                <XCircle className="w-3.5 h-3.5"/> Reject
              </Button>
            </>
          )}
          {status === "confirmed" && (
            <>
              <Button size="sm" variant="outline" data-testid={`ds-bonus-${t.id}`} onClick={() => onAction("bonus", t)} className="h-8 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1">
                <Gift className="w-3.5 h-3.5"/> {t.bonus_amount > 0 ? "Edit bonus" : "Add bonus"}
              </Button>
              <Button size="sm" variant="outline" data-testid={`ds-cancel-${t.id}`} onClick={() => onAction("cancel", t)} className="h-8 border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1">
                <Ban className="w-3.5 h-3.5"/> Cancel
              </Button>
            </>
          )}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-border/60">
          <div className="grid md:grid-cols-2 gap-3 pt-3 text-sm">
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-sky-300"/> <span className="font-mono">{t.customer_contact}</span></div>
            <div className="flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5 text-amber-300"/> Submitted: {(t.created_at || "").slice(0, 16).replace("T", " ")}</div>
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-300"/> Pickup: {t.pickup_location}</div>
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-rose-300"/> Drop: {t.drop_location}</div>
            <div className="flex items-center gap-2"><UsersIcon className="w-3.5 h-3.5 text-sky-300"/> {t.no_of_passengers} passengers · {t.no_of_days} days</div>
            <div className="flex items-center gap-2"><Gift className="w-3.5 h-3.5 text-amber-300"/> Offer: <span className="font-mono text-sky-300">{fmt(t.trip_offer_value)}</span> · Bonus: <span className="font-mono text-emerald-300">{fmt(t.bonus_amount || 0)}</span></div>
          </div>
          {t.cancellation_reason && (
            <div className="mt-3 p-2 rounded-md border border-rose-500/40 bg-rose-500/[0.06] text-xs text-rose-200">
              <span className="uppercase tracking-wider text-[10px] text-rose-300 mr-2">Cancellation reason:</span>{t.cancellation_reason}
            </div>
          )}
          {(t.audit_log || []).length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground mb-1"><History className="w-3 h-3"/> Audit trail</div>
              <div className="space-y-1">
                {t.audit_log.map((l, i) => (
                  <div key={i} className="text-[11px] text-slate-300 font-mono grid grid-cols-[auto_1fr] gap-2">
                    <span className="text-amber-300">{(l.ts || "").slice(0, 16).replace("T", " ")}</span>
                    <span><span className="text-emerald-300">{l.action}</span> · {l.actor_name}{l.amount != null ? ` · ₹${l.amount}` : ""}{l.note ? ` · ${l.note}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DriverSubmissions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bonusFor, setBonusFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelMode, setCancelMode] = useState("cancel");

  async function load() {
    try { const { data } = await api.get("/driver-trips"); setItems(data); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  useEffect(() => { load(); }, []);

  async function onAction(kind, t) {
    if (kind === "confirm") {
      try { await api.post(`/driver-trips/${t.id}/confirm`, { amount: 0 }); toast.success("Trip confirmed"); load(); }
      catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
    } else if (kind === "bonus") setBonusFor(t);
    else if (kind === "reject") { setCancelMode("reject"); setCancelFor(t); }
    else if (kind === "cancel") { setCancelMode("cancel"); setCancelFor(t); }
  }

  const filtered = useMemo(() => items.filter((t) => {
    if (statusFilter !== "all" && (t.status || "pending") !== statusFilter) return false;
    if (q && !JSON.stringify(t).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, q, statusFilter]);

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter((t) => (t.status || "pending") === "pending").length,
    confirmed: items.filter((t) => t.status === "confirmed").length,
    cancelled: items.filter((t) => t.status === "cancelled").length,
    total_bonus: items.reduce((s, t) => s + (t.status === "confirmed" ? (t.bonus_amount || 0) : 0), 0),
  }), [items]);

  return (
    <div>
      <PageHeader
        title="Driver Submissions"
        subtitle="Trips submitted by drivers from their portal. Confirm, reject, cancel, and manage per-trip bonus with an audit trail."
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          ["Total", counts.all, "sky"],
          ["Pending", counts.pending, "amber"],
          ["Confirmed", counts.confirmed, "emerald"],
          ["Cancelled", counts.cancelled, "rose"],
          ["Bonus paid", fmt(counts.total_bonus), "pink"],
        ].map(([label, val, tone]) => (
          <div key={label} data-testid={`ds-stat-${label.toLowerCase().replace(/\s/g,"-")}`}
            className={`p-4 rounded-lg border bg-[hsl(224_45%_9%)] border-${tone}-500/40 text-${tone}-300`}>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
            <div className="mt-1 text-2xl font-display font-bold">{val}</div>
          </div>
        ))}
      </div>

      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ds-search" placeholder="Search customer, driver, location…" className="bg-transparent border-0 focus-visible:ring-0 h-8 max-w-xs"/>
          <div className="flex items-center gap-1 ml-auto">
            {["all", "pending", "confirmed", "cancelled"].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`ds-filter-${s}`}
                className={`px-2.5 py-1 rounded-md text-xs uppercase ${statusFilter === s ? "bg-amber-500 text-[hsl(224_60%_6%)] font-semibold" : "bg-[hsl(224_40%_12%)] text-muted-foreground hover:text-white"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No submissions match this filter.</div>
          ) : filtered.map((t) => <TripRow key={t.id} t={t} onAction={onAction}/>)}
        </div>
      </Card>

      <BonusDialog trip={bonusFor} open={!!bonusFor} onOpenChange={(v) => !v && setBonusFor(null)} onSaved={load}/>
      <CancelDialog trip={cancelFor} open={!!cancelFor} onOpenChange={(v) => !v && setCancelFor(null)} onSaved={load} mode={cancelMode}/>
    </div>
  );
}
